// CTV-751: shared chrome for the CTVBuyer-branded standalone auth pages
// (staff sign-in at /admin, reset password, station-invite accept, access
// denied).
//
// Two variants:
//   • light (default) — soft brand-glow backdrop, ink wordmark. Used by the
//     transactional pages (reset password, invites, access denied).
//   • dark — the dashboard login's navy aurora treatment (same background
//     assets, copied into this app's public/), but deliberately composed
//     differently so it reads as a sibling of the dashboard, not the
//     dashboard itself: centered single column, white wordmark with a
//     "Publisher Portal" eyebrow, periwinkle CTAs inside the card. Used by
//     the /admin staff sign-in.
//
// Station-facing auth surfaces (SignInModal, InvitationLandingPage) stay
// station-branded and deliberately do NOT use this shell: advertisers know
// the station they buy from, not CTVBuyer.
//
// Brand colors mirror the dashboard login (frontend/src/pages/Login.tsx),
// which hard-codes them the same way. Defined once here for this app.

import React from 'react';

export const CTV_NAVY = '#00133c';
export const CTV_TEAL = '#00f7b0';
// Primary CTA pair for PrimaryButton: gradient runs accent → deep.
export const CTV_CTA_ACCENT = '#7e80ff';
export const CTV_CTA_DEEP = '#5f61e6';

interface CtvBuyerAuthShellProps {
  children: React.ReactNode;
  /** Card column width. Defaults to the auth-card standard. */
  maxWidthClass?: string;
  variant?: 'light' | 'dark';
  /** Teal tagline under the wordmark (dark variant only). Omit for surfaces
   *  that aren't publisher-facing, e.g. the shared reset-password page. */
  eyebrow?: string;
  /** Replaces the default CTVBuyer wordmark block above the card. Used by
   *  station-branded surfaces (advertiser invite) that ride this backdrop
   *  but lead with the station mark instead. */
  mark?: React.ReactNode;
}

const CtvBuyerAuthShell: React.FC<CtvBuyerAuthShellProps> = ({
  children,
  maxWidthClass = 'max-w-md',
  variant = 'light',
  eyebrow,
  mark,
}) => {
  const base = import.meta.env.BASE_URL;

  if (variant === 'dark') {
    return (
      <div
        className="relative min-h-screen flex items-center justify-center px-4 py-10 overflow-hidden"
        style={{ backgroundColor: CTV_NAVY }}
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <img
            src={`${base}login-aurora-bg.jpg`}
            alt=""
            className="absolute left-0 w-full h-full"
            style={{ objectFit: 'cover', objectPosition: 'center bottom' }}
          />
          <img
            src={`${base}login-gradient.svg`}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={`${base}login-grid.svg`}
              alt=""
              className="absolute"
              style={{
                width: '200%',
                height: 'auto',
                bottom: '-10%',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />
          </div>
        </div>
        <div className={`relative w-full ${maxWidthClass}`}>
          {mark ?? (
            <div className="flex flex-col items-center mb-8">
              <img
                src={`${base}ctvbuyer-logo.svg`}
                alt="CTVBuyer"
                className="h-10 w-auto object-contain"
              />
              {eyebrow && (
                <div
                  className="mt-3 text-lg font-semibold uppercase tracking-[0.18em]"
                  style={{ color: CTV_TEAL }}
                >
                  {eyebrow}
                </div>
              )}
            </div>
          )}
          {children}
          <p className="mt-6 text-center text-xs text-white/50">
            © {new Date().getFullYear()} CTVBuyer
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#f5f6fb] flex items-center justify-center px-4 py-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(600px 320px at 12% 0%, rgba(126,128,255,0.16), transparent 60%), radial-gradient(520px 280px at 88% 100%, rgba(0,247,176,0.12), transparent 60%)',
        }}
      />
      <div className={`relative w-full ${maxWidthClass}`}>
        <div className="flex justify-center mb-6">
          <img
            src={`${base}ctvbuyer-logo.svg`}
            alt="CTVBuyer"
            className="h-8 w-auto object-contain"
            // Source SVG is white; flatten to ink for the light backdrop.
            style={{ filter: 'brightness(0)' }}
          />
        </div>
        {children}
        <p className="mt-6 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} CTVBuyer
        </p>
      </div>
    </div>
  );
};

export default CtvBuyerAuthShell;
